import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { LoaderData } from "~/types";
import { INTENTS, loadCookiesData } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { message } from "antd";
import { t } from "i18next";
import {
  apiClient,
  AddBuildingPageLoaderResult,
} from "~/services/apiClient";

export const AddBuildingPageLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<AddBuildingPageLoaderType> => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) redirect("/explore");
  const userUID = user!.uid;
  const authAccessInfo = await loadCookiesData(userUID);
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const { searchParams } = new URL(request.url);
  const initialPropertyID =
    searchParams.get("propertyID") ?? searchParams.get("propertID") ?? "";

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await apiClient.loader<AddBuildingPageLoaderResult>(
    "add-building-page",
    {
      ownerID,
      ownerType,
      userID: userUID,
      initialPropertyID: initialPropertyID || undefined,
      isAdminOrSuperAdmin: String(isAdminOrSuperAdmin),
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load add building page:", result.error);
    throw new Error(result.error ?? "Failed to load add building page");
  }

  const data = result.data;
  const propertyList: LoaderData<APITypes.Property[]> = {
    success: true,
    data: data.propertyList,
  };

  return {
    propertyList,
    initialProperty: data.initialProperty,
    canCreateBuilding: data.canCreateBuilding,
    isAuthenticated: true,
    subscriptionPlan: data.subscriptionPlan,
    currentBuildingCount: data.currentBuildingCount,
    maxBuildings: data.maxBuildings,
  };
};

export type AddBuildingPageLoaderType = {
  propertyList: LoaderData<APITypes.Property[]>;
  initialProperty: APITypes.Property;
  canCreateBuilding: boolean;
  isAuthenticated: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  currentBuildingCount: number;
  maxBuildings: number;
};

/** Lambda add-building action result */
interface AddBuildingActionResult {
  created: number;
  id?: string;
  propertyID?: string;
  redirectMultiple?: boolean;
  redirectSingle?: boolean;
}

export const AddBuildingPageAction = async ({
  request,
}: LoaderFunctionArgs) => {
  const formData = await request.formData();
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");
  const { role } = authAccessInfo.data;

  const payload = JSON.parse(
    formData.get("body") as string
  ) as unknown as APITypes.CreateBuildingInput & { isAdminOrSuperAdmin?: boolean };
  const propertyID = payload.propertyID;
  payload.status = APITypes.BuildingStatusType.None;
  payload.isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  return withEntityCacheInvalidation([{ entity: "building" }], async () => {
    const result = await apiClient.sendIntent<AddBuildingActionResult>(
      INTENTS._add_building,
      payload,
    );

    if (!result.success) {
      message.error(t("Error_creating_building"));
      console.error("Add building failed:", result.error);
      return redirect(propertyID ? `/property/${propertyID}` : "/explore");
    }

    const data = result.data;
    if (data?.redirectMultiple && data.propertyID) {
      const count = data.created ?? 0;
      message.success(t("Buildings_created_successfully"), count);
      return redirect(`/property/${data.propertyID}`);
    }
    if (data?.redirectSingle && data.id) {
      message.success(t("Building_created_successfully"));
      return redirect(`/building/${data.id}`);
    }

    message.error(t("Error_creating_building"));
    return redirect(propertyID ? `/property/${propertyID}` : "/explore");
  });
};
